import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AppSetting } from "@shared/schema";
import { X } from "lucide-react";

type FilterOptions = {
  status: string;
  category: string;
  vendors: string[];
};

type ApplicationFilterDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilterChange: (filters: FilterOptions) => void;
  currentFilters: FilterOptions;
  applications: any[];
};

export default function ApplicationFilterDialog({
  open,
  onOpenChange,
  onFilterChange,
  currentFilters,
  applications,
}: ApplicationFilterDialogProps) {
  // Local state for filter values
  const [status, setStatus] = useState<string>(currentFilters.status || "all");
  const [category, setCategory] = useState<string>(currentFilters.category || "all");
  const [selectedVendors, setSelectedVendors] = useState<string[]>(currentFilters.vendors || []);
  
  // Get settings from API - specifically fetch categories and status
  const { data: rawCategorySettings } = useQuery<AppSetting[]>({
    queryKey: ['/api/app-settings', { type: 'category' }],
    queryFn: async () => {
      const res = await fetch('/api/app-settings?type=category');
      if (!res.ok) throw new Error('Failed to fetch category settings');
      return res.json();
    },
  });
  const categorySettings = Array.isArray(rawCategorySettings) ? rawCategorySettings : [];

  const { data: rawStatusSettings } = useQuery<AppSetting[]>({
    queryKey: ['/api/app-settings', { type: 'status' }],
    queryFn: async () => {
      const res = await fetch('/api/app-settings?type=status');
      if (!res.ok) throw new Error('Failed to fetch status settings');
      return res.json();
    },
  });
  const statusSettings = Array.isArray(rawStatusSettings) ? rawStatusSettings : [];

  // Get categories and status options
  const categoryOptions = categorySettings
    .filter(setting => setting.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(setting => setting.value);

  const statusOptions = statusSettings
    .filter(setting => setting.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map(setting => setting.value);
    
  // Get unique vendor list from applications
  const uniqueVendors = new Set<string>();
  (Array.isArray(applications) ? applications : []).forEach(app => {
    if (app.vendor) {
      uniqueVendors.add(app.vendor);
    }
  });
  const vendorOptions = Array.from(uniqueVendors).sort();
  
  // Handle category change
  const handleCategoryChange = (value: string) => {
    setCategory(value);
  };
  
  // Handle status change
  const handleStatusChange = (value: string) => {
    setStatus(value);
  };
  
  // Handle vendor selection
  const handleSelectVendor = (vendor: string) => {
    if (selectedVendors.includes(vendor)) {
      setSelectedVendors(selectedVendors.filter(v => v !== vendor));
    } else {
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };
  
  // Sync local state with currentFilters when dialog opens
  useEffect(() => {
    if (open) {
      setStatus(currentFilters.status || "all");
      setCategory(currentFilters.category || "all");
      setSelectedVendors(currentFilters.vendors || []);
    }
  }, [open, currentFilters]);
  
  // Apply filters
  const applyFilters = () => {
    onFilterChange({
      status,
      category,
      vendors: selectedVendors,
    });
    onOpenChange(false);
  };
  
  // Reset filters
  const resetFilters = () => {
    setStatus("all");
    setCategory("all");
    setSelectedVendors([]);
  };
  
  // Clear all and close
  const clearAllFilters = () => {
    const defaultFilters: FilterOptions = {
      status: "all",
      category: "all",
      vendors: [],
    };
    onFilterChange(defaultFilters);
    onOpenChange(false);
  };
  
  // Count active filters
  const countActiveFilters = () => {
    let count = 0;
    if (status !== "all") count++;
    if (category !== "all") count++;
    if (selectedVendors.length > 0) count++;
    return count;
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Filter Applications</DialogTitle>
            {countActiveFilters() > 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs h-7 px-2"
                onClick={clearAllFilters}
              >
                Clear All
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Active filters summary */}
          {countActiveFilters() > 0 && (
            <div className="bg-slate-50 px-3 py-2 rounded-md">
              <div className="text-sm font-medium mb-2">Active Filters:</div>
              <div className="flex flex-wrap gap-2">
                {status !== "all" && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Status: {status}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setStatus("all")} />
                  </Badge>
                )}
                {category !== "all" && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Category: {category}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setCategory("all")} />
                  </Badge>
                )}
                {selectedVendors.map(vendor => (
                  <Badge key={vendor} variant="secondary" className="flex items-center gap-1">
                    Vendor: {vendor}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleSelectVendor(vendor)} />
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Status filter */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger id="status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map(option => (
                  <SelectItem key={option} value={option.toLowerCase()}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Category filter */}
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={handleCategoryChange}>
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categoryOptions.map(option => (
                  <SelectItem key={option} value={option.toLowerCase()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Vendor filter */}
          <div className="space-y-2">
            <Label>Vendors</Label>
            <div className="border rounded-md p-3 max-h-[150px] overflow-y-auto">
              {vendorOptions.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-2">
                  No vendors available
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {vendorOptions.map(vendor => (
                    <Badge 
                      key={vendor}
                      variant={selectedVendors.includes(vendor) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => handleSelectVendor(vendor)}
                    >
                      {vendor}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={resetFilters} type="button">
            Reset Form
          </Button>
          <Button onClick={applyFilters}>Apply Filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}